import { useEffect, useMemo, useRef, useState } from 'react'
import { parse } from 'yaml'

const PRIMARY_COLOR = '#275bab'

const FLATTENED_CATEGORY_IDS = new Set([
  'd9e5cf7c-0048-4fbc-b377-219b9ca2df04',
  '53e5e7d1-5960-4f26-b079-f1edfc9e4b80',
])

const GROUPED_CATEGORY_RULES = {
  '97fef0b0-fd93-4eb6-9445-518801347c5d': {
    groupId: 'beyond-electricity',
    groupTitle: 'Beyond electricity',
    insertAfterSectionId: 'f2ae4c79-b91e-4139-b6b4-c51d8eb6f90a',
    sectionIds: new Set([
      'b027ce84-5aa3-496f-bf82-45558651fa70',
      'f2bd4789-01fe-4752-9411-1040dcbe3626',
      'a96b19ba-229b-4a15-aa7b-efcb9fb114c0',
    ]),
  },
}

const FALLBACK_INTRO =
  'This is a short introductory lead text to explain the purpose of the website. Here you can find descriptive audio tracks to guide visitors through the exhibition.'

const extractText = (html) => {
  if (!html) {
    return ''
  }

  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim()
}

const getAudioData = (stop) => {
  const fileBaseFields =
    stop?.custom_fields?.field_audio?.[0]?.custom_fields?.field_media_document?.[0]
      ?.target_entity?.base_fields

  return {
    url: fileBaseFields?.url || null,
    name: fileBaseFields?.name || null,
  }
}

const normalizeStops = (section) =>
  (section?.custom_fields?.field_subsections || []).map((stop) => {
    const audio = getAudioData(stop)

    return {
      id: stop?.uuid,
      title: stop?.custom_fields?.field_title?.[0]?.value || 'Untitled',
      audioUrl: audio.url,
      audioName: audio.name,
    }
  })

const normalizeSections = (sections, fallbackColor) =>
  Object.values(sections || {}).map((section) => ({
    id: section?.uuid,
    title: section?.base_fields?.title || 'Untitled section',
    lead: extractText(section?.custom_fields?.field_body?.[0]?.value),
    color: section?.custom_fields?.field_colorcode?.[0]?.value || fallbackColor || '#8fcbf9',
    stops: normalizeStops(section),
  }))

const normalizeCategories = (bundle) =>
  Object.values(bundle?.items || {}).map((item) => ({
    id: item?.uuid,
    title: item?.title || 'Untitled category',
    color: item?.color || '#8fcbf9',
    sections: normalizeSections(item?.subsections, item?.color),
  }))

const ChevronRight = ({ className = 'h-8 w-8 sm:h-10 sm:w-10' }) => (
  <svg id="Ebene_1" xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 70.5 62.8" className="w-auto h-4">
    <path class="st0" d="M37.7,1.4l30,30-30,30"  style={{fill: "none", "stroke": "#fff", "strokeWidth": "4px"}}/>
    <line class="st1" x1="67.7" y1="31.4" x2="3.3" y2="31.4" style={{fill: "none", "stroke": "#fff", "strokeWidth": "4px"}}/>
  </svg>
)

const ArrowLeft = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path
      d="M15 6L9 12L15 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const formatTitle = (title) => {
  const parts = title.split(/\s(?=Audio Guide$)/)
  return parts.length > 1 ? parts : [title]
}

const DrilldownRow = ({ title, color, onClick, indexLabel, hideMarker = false }) => (
  
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex justify-between items-center gap-4 border-b border-white/10 px-0 py-4 text-left transition last:border-b-0 focus:outline-none focus:none sm:gap-6 sm:py-7`} 
  >
    {!hideMarker && color ? (
      <span
        className="h-9 w-4 rounded-sm shadow-[0_0_0_1px_rgba(255,255,255,0.05)] sm:h-9 sm:w-4"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
    ) : (
      <span className="text-xs font-semibold tracking-[0.22em] sr-only text-white/45">{ indexLabel}</span>
    )}
    <span className="text-[1.35rem] font-semibold leading-none text-white grow sm:text-[1.35rem]">
      {title}
    </span>
    <span className="text-white/95">
      <ChevronRight />
    </span>
  </button>
)

const AudioStopCard = ({ stop, index, onStartPlayback }) => (
  <article className="border-b border-white/10 py-5 last:border-b-0 sm:py-7">
    <div className="grid grid-cols-[auto_1fr] items-start gap-4 sm:gap-6">
      {/* <span className="pt-1 text-xs font-semibold text-white/45">
        {String(index + 1).padStart(2, '0')}
      </span> */}
      <div className="min-w-0">
        <h3 className="text-[1.35rem] font-semibold leading-none text-white sm:text-[1.35rem]">
          {stop.title}
        </h3>

        <div className="mt-5">
          {stop.audioUrl ? (
            <AudioPlayer stop={stop} onStartPlayback={onStartPlayback} />
          ) : (
            <p className="text-base leading-7 text-white/70">
              Audio file not available yet for this element.
            </p>
          )}
        </div>
      </div>
    </div>
  </article>
)

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds)) {
    return '0:00'
  }

  const totalSeconds = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60

  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`
}

const AudioPlayer = ({ stop, onStartPlayback }) => {
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const audio = audioRef.current

    if (!audio) {
      return undefined
    }

    const handleLoadedMetadata = () => setDuration(audio.duration || 0)
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0)
    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(audio.duration || 0)
    }
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
    }
  }, [])

  useEffect(() => {
    const audio = audioRef.current

    if (!audio) {
      return
    }

    audio.pause()
    audio.currentTime = 0
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
  }, [stop.audioUrl])

  const togglePlayback = async () => {
    const audio = audioRef.current

    if (!audio) {
      return
    }

    if (audio.paused) {
      try {
        onStartPlayback?.(audio)
        await audio.play()
      } catch {
        setIsPlaying(false)
      }
      return
    }

    audio.pause()
  }

  const handleSeek = (event) => {
    const audio = audioRef.current
    const nextTime = Number(event.target.value)

    if (!audio) {
      return
    }

    audio.currentTime = nextTime
    setCurrentTime(nextTime)
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div>
      <audio ref={audioRef} preload="metadata">
        <source src={stop.audioUrl} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={togglePlayback}
            className="flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg transition active:brightness-90 active:scale-[90%] focus:outline-none focus:none"
            style={{ backgroundColor: PRIMARY_COLOR }}
            aria-label={isPlaying ? `Pause ${stop.title}` : `Play ${stop.title}`}
          >
            {isPlaying ? (
              <span className="flex gap-1">
                <span className="h-5 w-1.5 rounded-sm bg-white" />
                <span className="h-5 w-1.5 rounded-sm bg-white" />
              </span>
            ) : (
              <span
                className="ml-1 h-0 w-0 border-y-[10px] border-l-[16px] border-y-transparent border-l-white"
                aria-hidden="true"
              />
            )}
          </button>
          <div>
            <input
              type="range"
              min="0"
              max={duration || 0}
              step="0.1"
              value={Math.min(currentTime, duration || 0)}
              onChange={handleSeek}
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-white/10 accent-[#275bab]"
              style={{
                background: `linear-gradient(to right, ${PRIMARY_COLOR} 0%, ${PRIMARY_COLOR} ${progress}%, rgba(255,255,255,0.1) ${progress}%, rgba(255,255,255,0.1) 100%)`,
              }}
              aria-label={`Seek ${stop.title}`}
            />
          </div>
          <div className="text-sm text-white/60">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>        
      </div>


    </div>
  )
}

const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="mb-6 px-4 sm:px-6">
    <ol className="flex flex-wrap items-center gap-2 text-sm text-white/55">
      {items.map((item, index) => (
        <li key={`${item.label}-${index}`} className="flex items-center gap-2">
          {index > 0 ? <span className="text-white/35">/</span> : null}
          {item.onClick ? (
            <button
              type="button"
              onClick={item.onClick}
              className="transition hover:text-white focus:outline-none focus:text-white"
            >
              {item.label}
            </button>
          ) : (
            <span className="text-white">{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
)

const isInteractiveElement = (target) =>
  target instanceof Element &&
  Boolean(target.closest('button, input, audio, a, [role="button"]'))

function App() {
  const [bundle, setBundle] = useState(null)
  const [error, setError] = useState('')
  const [selectedCategoryId, setSelectedCategoryId] = useState(null)
  const [selectedGroupId, setSelectedGroupId] = useState(null)
  const [selectedSectionId, setSelectedSectionId] = useState(null)
  const swipeStateRef = useRef({
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    tracking: false,
  })
  const activeAudioRef = useRef(null)

  useEffect(() => {
    let isMounted = true

    const loadBundle = async () => {
      try {
        const response = await fetch('./data/bundle.yml')

        if (!response.ok) {
          throw new Error(`Failed to load bundle (${response.status})`)
        }

        const text = await response.text()
        const parsedBundle = parse(text)

        if (!isMounted) {
          return
        }

        setBundle(parsedBundle)
        setError('')
      } catch (loadError) {
        if (!isMounted) {
          return
        }

        setError(loadError instanceof Error ? loadError.message : 'Failed to load content')
      }
    }

    loadBundle()

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [selectedCategoryId, selectedGroupId, selectedSectionId])

  const categories = useMemo(() => normalizeCategories(bundle), [bundle])

  const selectedCategory =
    categories.find((category) => category.id === selectedCategoryId) || null
  const groupedCategoryRule = selectedCategory
    ? GROUPED_CATEGORY_RULES[selectedCategory.id]
    : null
  const groupedSections = groupedCategoryRule
    ? selectedCategory.sections.filter((section) => groupedCategoryRule.sectionIds.has(section.id))
    : []
  const visibleCategorySections = groupedCategoryRule
    ? selectedCategory.sections.filter((section) => !groupedCategoryRule.sectionIds.has(section.id))
    : selectedCategory?.sections || []
  const categoryNavigationItems = visibleCategorySections.flatMap((section) => {
    const items = [{ type: 'section', section }]

    if (
      groupedCategoryRule &&
      groupedSections.length &&
      section.id === groupedCategoryRule.insertAfterSectionId
    ) {
      items.push({ type: 'group', id: groupedCategoryRule.groupId })
    }

    return items
  })
  const hasInsertedGroup = categoryNavigationItems.some((item) => item.type === 'group')

  if (groupedCategoryRule && groupedSections.length && !hasInsertedGroup) {
    categoryNavigationItems.push({ type: 'group', id: groupedCategoryRule.groupId })
  }

  const selectedGroup =
    groupedCategoryRule && selectedGroupId === groupedCategoryRule.groupId
      ? {
          id: groupedCategoryRule.groupId,
          title: groupedCategoryRule.groupTitle,
          sections: groupedSections,
        }
      : null

  const selectedSection =
    selectedCategory?.sections.find((section) => section.id === selectedSectionId) || null
  const isFlattenedCategory =
    selectedCategory && FLATTENED_CATEGORY_IDS.has(selectedCategory.id)
  const flattenedSection = isFlattenedCategory ? selectedCategory.sections[0] : null

  const currentLevel = selectedSection
    ? 'section'
    : selectedGroup
      ? 'group'
    : selectedCategory
      ? 'category'
      : 'home'

  const projectTitle = bundle?.project?.title || 'IAEA Visitor Centre Audio Guide'
  const projectLead = extractText(bundle?.project?.body)
  const handleStartPlayback = (nextAudio) => {
    if (activeAudioRef.current && activeAudioRef.current !== nextAudio) {
      activeAudioRef.current.pause()
    }

    activeAudioRef.current = nextAudio
  }

  const goBackOneLevel = () => {
    if (selectedSection) {
      setSelectedSectionId(null)
      return
    }

    if (selectedGroup) {
      setSelectedGroupId(null)
      return
    }

    if (selectedCategory) {
      setSelectedCategoryId(null)
    }
  }

  const handleTouchStart = (event) => {
    if (event.touches.length !== 1 || isInteractiveElement(event.target)) {
      swipeStateRef.current = {
        startX: 0,
        startY: 0,
        lastX: 0,
        lastY: 0,
        tracking: false,
      }
      return
    }

    swipeStateRef.current = {
      startX: event.touches[0].clientX,
      startY: event.touches[0].clientY,
      lastX: event.touches[0].clientX,
      lastY: event.touches[0].clientY,
      tracking: true,
    }
  }

  const handleTouchMove = (event) => {
    if (!swipeStateRef.current.tracking || event.touches.length !== 1) {
      return
    }

    swipeStateRef.current = {
      ...swipeStateRef.current,
      lastX: event.touches[0].clientX,
      lastY: event.touches[0].clientY,
    }
  }

  const handleTouchEnd = (event) => {
    const { startX, startY, lastX, lastY, tracking } = swipeStateRef.current

    swipeStateRef.current = {
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0,
      tracking: false,
    }

    if (!tracking || currentLevel === 'home' || event.changedTouches.length !== 1) {
      return
    }

    const endX = lastX || event.changedTouches[0].clientX
    const endY = lastY || event.changedTouches[0].clientY
    const deltaX = endX - startX
    const deltaY = endY - startY
    const absDeltaX = Math.abs(deltaX)
    const absDeltaY = Math.abs(deltaY)

    if (deltaX > 80 && absDeltaX > absDeltaY * 1.25) {
      goBackOneLevel()
    }
  }

  const breadcrumbItems = [
    {
      label: 'Home',
      onClick:
        currentLevel !== 'home'
          ? () => {
              setSelectedCategoryId(null)
              setSelectedGroupId(null)
              setSelectedSectionId(null)
            }
          : null,
    },
    ...(selectedCategory
      ? [
          {
            label: selectedCategory.title,
            onClick:
              currentLevel === 'group' || currentLevel === 'section'
                ? () => {
                    setSelectedGroupId(null)
                    setSelectedSectionId(null)
                  }
                : null,
          },
        ]
      : []),
    ...(selectedGroup
      ? [
          {
            label: selectedGroup.title,
            onClick:
              currentLevel === 'section'
                ? () => {
                    setSelectedSectionId(null)
                  }
                : null,
          },
        ]
      : []),
    ...(selectedSection
      ? [
          {
            label: selectedSection.title,
            onClick: null,
          },
        ]
      : []),
  ]
  const activeHeader = selectedSection
    ? {
        title: selectedSection.title,
        lead: selectedSection.lead || '',
        eyebrow: selectedGroup?.title || selectedCategory?.title || '',
        color: selectedSection.color,
        onBack: goBackOneLevel,
      }
    : selectedGroup
      ? {
          title: selectedGroup.title,
          lead: 'Choose a topic to explore.',
          eyebrow: selectedCategory?.title || '',
          color: selectedCategory?.color || PRIMARY_COLOR,
          onBack: goBackOneLevel,
        }
    : selectedCategory
      ? {
          title: selectedCategory.title,
          lead:
            flattenedSection?.lead ||
            selectedCategory.sections[0]?.lead ||
            'Browse the topics in this field to continue.',
          eyebrow: 'Section',
          color: selectedCategory.color,
          onBack: goBackOneLevel,
        }
      : {
          title: projectTitle,
          lead:
            projectLead || categories[0]?.sections?.[0]?.lead || FALLBACK_INTRO,
          eyebrow: '',
          color: PRIMARY_COLOR,
          onBack: null,
        }

  return (
    <>
    <main
      className="min-h-screen bg-[#333233] text-stone-50 flex flex-col"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <section className="relative mx-auto w-full pb-6 pt-4 grow">
        <Breadcrumbs items={breadcrumbItems} />

        {activeHeader.onBack && false ? (
          <button
            type="button"
            onClick={activeHeader.onBack}
            className="mb-8 mt-4 ml-6 inline-flex items-center gap-2 rounded-full px-8 pl-6 py-2 text-base font-medium text-white transition hover:brightness-90 rounded-full text-white shadow-lg transition active:brightness-90 active:scale-[90%] focus:outline-none focus:none"
            style={{ backgroundColor: PRIMARY_COLOR }}
          >
            <ArrowLeft />
            Back
          </button>
        ) : null}

        <header className="relative pl-0 sm:pl-0">
          <div className="flex items-start gap-4 sm:gap-6">
            <span
              className="mt-1 h-16 w-6 shrink-0 sm:h-20 sm:w-8"
              style={{ backgroundColor: activeHeader.color }}
              aria-hidden="true"
            />
            <div className="min-w-0">
          {activeHeader.eyebrow ? (
            <p className="mb-3 text-xs font-semibold uppercase text-white/45">
              {activeHeader.eyebrow}
            </p>
          ) : null}

              <h1 className="flex flex-col gap-1 pr-6 text-[2rem] font-semibold leading-[1.25] sm:text-[2rem]">
                {formatTitle(activeHeader.title).map((part) => (
                  <span key={part}>{part}</span>
                ))}
              </h1>

            </div>
          </div>
        </header>
        <p className="mt-12 text-[1.35rem] px-4 leading-[1.35] text-white font-light sm:mt-16 sm:text-[1.35rem]">
              {activeHeader.lead}
        </p>
        <section className="mt-10 sm:mt-10 mr-4 mb-4">
          <h2 className="mb-2 text-[1rem] px-4 font-semibold text-stone-50 sm:text-[1rem]">
            {currentLevel === 'home'
              ? 'Please choose a field to start:'
              : (currentLevel === 'category' && !isFlattenedCategory) || currentLevel === 'group'
                ? 'Choose a topic to explore:'
                : currentLevel === 'section'
                  ? 'Please choose a chapter to listen to:'
                  : 'Please choose a chapter to listen to:'}
          </h2>

          {error ? (
            <p className="rounded-2xl border border-red-300/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
              {error}
            </p>
          ) : null}

          {!error && !categories.length ? (
            <p className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/75">
              Loading audio guide content...
            </p>
          ) : null}

          {!error && categories.length ? (
            <div className="overflow-hidden px-4 sm:px-6">
              {currentLevel === 'home' ? (
                <nav aria-label="Audioguide fields">
                  {categories.map((category) => (
                    <DrilldownRow
                      key={category.id}
                      title={category.title}
                      color={category.color}
                      onClick={() => {
                        setSelectedCategoryId(category.id)
                        setSelectedGroupId(null)
                        setSelectedSectionId(null)
                      }}
                    />
                  ))}
                </nav>
              ) : null}

              {currentLevel === 'category' && selectedCategory && !isFlattenedCategory ? (
                <div className="">
                  <nav aria-label={`${selectedCategory.title} sections`}>
                    {categoryNavigationItems.map((item) =>
                      item.type === 'group' ? (
                        <DrilldownRow
                          key={item.id}
                          title={groupedCategoryRule.groupTitle}
                          hideMarker
                          onClick={() => {
                            setSelectedGroupId(groupedCategoryRule.groupId)
                            setSelectedSectionId(null)
                          }}
                        />
                      ) : (
                        <DrilldownRow
                          key={item.section.id}
                          title={item.section.title}
                          color={item.section.color}
                          hideMarker
                          onClick={() => {
                            setSelectedGroupId(null)
                            setSelectedSectionId(item.section.id)
                          }}
                        />
                      ),
                    )}
                  </nav>
                </div>
              ) : null}

              {currentLevel === 'group' && selectedCategory && selectedGroup ? (
                <div className="">
                  <nav aria-label={`${selectedGroup.title} sections`}>
                    {selectedGroup.sections.map((section) => (
                      <DrilldownRow
                        key={section.id}
                        title={section.title}
                        color={section.color}
                        hideMarker
                        onClick={() => {
                          setSelectedSectionId(section.id)
                        }}
                      />
                    ))}
                  </nav>
                </div>
              ) : null}

              {currentLevel === 'category' && selectedCategory && flattenedSection ? (
                <div className="">
                  <div aria-label={`${selectedCategory.title} audio stops`}>
                    {flattenedSection.stops.map((stop, index) => (
                      <AudioStopCard
                        key={stop.id}
                        stop={stop}
                        index={index}
                        onStartPlayback={handleStartPlayback}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {currentLevel === 'section' && selectedCategory && selectedSection ? (
                <div className="">
                  <div aria-label={`${selectedSection.title} audio stops`}>
                    {selectedSection.stops.map((stop, index) => (
                      <AudioStopCard
                        key={stop.id}
                        stop={stop}
                        index={index}
                        onStartPlayback={handleStartPlayback}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      </section>
      <section className="mb-8 mt-4 mx-4 flex items-center gap-2 justify-between">
      {activeHeader.onBack ? (
          <button
            type="button"
            onClick={activeHeader.onBack}
            className="inline-flex items-center gap-2 rounded-full px-8 pl-6 py-2 text-base font-medium text-white transition hover:brightness-90 rounded-full text-white shadow-lg transition active:brightness-90 active:scale-[90%] focus:outline-none focus:none"
            style={{ backgroundColor: PRIMARY_COLOR }}
          >
            <ArrowLeft />
            Back
          </button>
        ) : <div></div>}
        <div className="flex items-center">
        <a href="https://www.iaea.org/about/terms-of-use" className="text-xs text-white/50 hover:text-white/25 underline transition inline-block" target="_blank" rel="noopener noreferrer">Terms of Use</a>
        </div>
      </section>
      <footer className="border-t border-white/10 px-4 py-6 text-center text-sm text-white/50 sm:px-6 bg-[#C6C6C6]">
          <a href="https://www.iaea.org" className="button rounded-full bg-white/30 px-6 py-2 text-black hover:bg-white/40" target="_blank" rel="noopener noreferrer">iaea.org</a>
      </footer>
    </main>
    
    </>
  )
}

export default App
